/**
 * Adrop 광고 서비스 유틸리티
 * Adrop SDK 및 REST API를 사용하여 광고를 요청하고 관리하는 서비스
 */

// Adrop 앱키 (adrop_service.json에서 가져옴)
const ADROP_APP_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcHBJZCI6IjAxSzVYM0cyUlo5V0EyQjRCTTRBWTVTTTBEOjAxSzYzR01RSlc3VllXTkVWMjFWME5UVkdZIiwicGxhdGZvcm0iOiJ3ZWIiLCJrZXlUeXBlIjoxLCJpYXQiOjE3NTg5MDcyMjcsImV4cCI6MzMzNjc0NDAyN30.fqmwFP7gHwzFiC9rKX5OjwyGl1B6SU9Bf8p0n-Dsx6s';

// Adrop REST API 설정
const ADROP_API_BASE_URL = 'https://api-v2.adrop.io';

// 광고 유닛 ID들
export const AD_UNITS = {
  WELLNESS_BANNER: '01K7KE18FV949B4RMEV3FWFP0R', // 웰니스 하단 배너 (320x50)
  TEST_BANNER: 'test_banner' // 테스트용 유닛 ID
};

/**
 * Adrop SDK 초기화
 */
export const initializeAdrop = () => {
  if (window.adrop) {
    try {
      window.adrop.initialize(ADROP_APP_KEY);
      return true;
    } catch (error) {
      console.error('❌ Adrop 초기화 실패:', error);
      return false;
    }
  } else {
    console.warn('⚠️ Adrop SDK가 로드되지 않았습니다.');
    return false;
  }
};

/**
 * 광고 요청
 * @param {string} unitId - 광고 유닛 ID
 * @returns {Promise<{ad: string, unit: string, message: string}>}
 */
export const requestAd = async (unitId) => {
  if (!window.adrop) {
    throw new Error('Adrop SDK가 로드되지 않았습니다.');
  }

  try {
    const result = await window.adrop.request(unitId);
    return result;
  } catch (error) {
    console.error('❌ 광고 요청 실패:', error);
    throw error;
  }
};

/**
 * 사용자 속성 설정
 * @param {string} key - 속성 키
 * @param {string} value - 속성 값
 * @returns {Promise<void>}
 */
export const setUserProperty = async (key, value) => {
  if (!window.adrop) {
    throw new Error('Adrop SDK가 로드되지 않았습니다.');
  }

  try {
    await window.adrop.setProperty(key, value);
  } catch (error) {
    console.error('❌ 사용자 속성 설정 실패:', error);
    throw error;
  }
};

/**
 * Adrop SDK 사용 가능 여부 확인
 * @returns {boolean}
 */
export const isAdropAvailable = () => {
  return typeof window.adrop !== 'undefined';
};

/**
 * REST API를 사용하여 배너 광고 요청
 * @param {string} unitId - 광고 유닛 ID
 * @param {string} userId - 사용자 ID (선택사항)
 * @param {string} platform - 플랫폼 (기본값: 'web')
 * @param {string} locale - 로케일 (기본값: 'ko')
 * @returns {Promise<{format: string, unit: string, w: number, h: number, ad: string}>}
 */
export const requestBannerAd = async (unitId, userId = null, platform = 'web', locale = 'ko') => {
  try {
    // URL 파라미터 구성
    const params = new URLSearchParams({
      unit: unitId,
      pf: platform,
      lcl: locale
    });
    
    // 사용자 ID가 있으면 추가
    if (userId) {
      params.append('uid', userId);
    }
    
    const response = await fetch(`${ADROP_API_BASE_URL}/request?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': ADROP_APP_KEY,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.code !== 0) {
      throw new Error(`Adrop API Error ${data.code}: ${data.msg}`);
    }
    
    return data.result;
    
  } catch (error) {
    console.error('❌ 배너 광고 요청 실패:', error);
    throw error;
  }
};

/**
 * REST API를 사용하여 사용자 프로퍼티 전송
 * @param {string} userId - 사용자 ID
 * @param {Object} properties - 사용자 속성 객체
 * @returns {Promise<boolean>} 성공 여부
 */
export const sendUserProperty = async (userId, properties) => {
  try {
    if (!userId || !properties) {
      console.warn('⚠️ Adrop 프로퍼티 전송 실패: userId 또는 properties가 없습니다');
      return false;
    }

    const response = await fetch(`${ADROP_API_BASE_URL}/property`, {
      method: 'POST',
      headers: {
        'Authorization': ADROP_APP_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        uid: userId,
        value: JSON.stringify(properties)
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return true;

  } catch (error) {
    console.error('❌ Adrop 사용자 프로퍼티 전송 실패:', error);
    return false;
  }
};

/**
 * REST API를 사용하여 이벤트 전송
 * @param {string} eventKey - 이벤트 키
 * @param {string} userId - 사용자 ID
 * @param {Object} eventData - 이벤트 데이터
 * @returns {Promise<boolean>} 성공 여부
 */
export const sendEvent = async (eventKey, userId, eventData = {}) => {
  try {
    if (!eventKey || !userId) {
      console.warn('⚠️ Adrop 이벤트 전송 실패: eventKey 또는 userId가 없습니다');
      return false;
    }

    const response = await fetch(`${ADROP_API_BASE_URL}/event`, {
      method: 'POST',
      headers: {
        'Authorization': ADROP_APP_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        key: eventKey,
        uid: userId,
        value: JSON.stringify(eventData)
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return true;

  } catch (error) {
    console.error('❌ Adrop 이벤트 전송 실패:', error);
    return false;
  }
};

/**
 * 사용자 데이터를 Adrop 프로퍼티 형식으로 변환
 * Adrop 요구사항에 맞는 정확한 포맷으로 변환
 * @param {Object} userData - 사용자 데이터
 * @returns {Object} Adrop 프로퍼티 객체
 */
export const convertUserDataToAdropProperties = (userData) => {
  if (!userData) return {};

  const properties = {};

  // 사용자 ID (USER_ID): 꼬끼오 사용자 고유 ID
  if (userData.user_id) {
    properties.USER_ID = String(userData.user_id);
  }

  // 나이 (AGE): 정수 문자열 형태 - user_kakao_year에서 계산
  if (userData.age !== undefined && userData.age !== null) {
    properties.AGE = String(userData.age);
  } else if (userData.user_kakao_year) {
    // 꼬끼오 실제 데이터: user_kakao_year에서 연나이 계산
    const currentYear = new Date().getFullYear();
    const birthYear = parseInt(userData.user_kakao_year);
    const calculatedAge = currentYear - birthYear + 1; // 연나이 계산 (태어난 해 포함)
    if (calculatedAge > 0 && calculatedAge < 150) {
      properties.AGE = String(calculatedAge);
    }
  } else if (userData.birth_year) {
    // 기존 birth_year 필드도 지원
    const currentYear = new Date().getFullYear();
    const calculatedAge = currentYear - parseInt(userData.birth_year);
    if (calculatedAge > 0 && calculatedAge < 150) {
      properties.AGE = String(calculatedAge);
    }
  }

  // 생년월일 (BIRTH): "yyyy", "yyyyMM", "yyyyMMdd" 형태
  if (userData.user_kakao_year) {
    // 꼬끼오 실제 데이터: user_kakao_year를 "yyyy" 형태로 전송
    properties.BIRTH = String(userData.user_kakao_year);
  } else if (userData.birth_year) {
    // 기존 birth_year 필드도 지원
    properties.BIRTH = String(userData.birth_year);
  } else if (userData.birth_date) {
    // 전체 생년월일이 있는 경우
    const birthDate = new Date(userData.birth_date);
    if (!isNaN(birthDate.getTime())) {
      const year = birthDate.getFullYear();
      const month = String(birthDate.getMonth() + 1).padStart(2, '0');
      const day = String(birthDate.getDate()).padStart(2, '0');
      
      // 완전한 날짜가 있으면 "yyyyMMdd" 형태
      if (userData.birth_date.includes('-') && userData.birth_date.split('-').length === 3) {
        properties.BIRTH = `${year}${month}${day}`;
      } else {
        // 년월만 있으면 "yyyyMM" 형태
        properties.BIRTH = `${year}${month}`;
      }
    }
  }

  // 성별 (GDR): "M"(남성), "F"(여성), "O"(기타), "U"(알 수 없음)
  // 꼬끼오 실제 데이터: user_gender 필드 사용
  const genderField = userData.user_gender || userData.gender;
  if (genderField) {
    const gender = String(genderField).toLowerCase().trim();
    
    // 남성 매핑
    if (gender === 'male' || gender === 'm' || gender === '남' || gender === '남성') {
      properties.GDR = 'M';
    }
    // 여성 매핑 (꼬끼오 주요 케이스: 'female', '여성')
    else if (gender === 'female' || gender === 'f' || gender === '여' || gender === '여성') {
      properties.GDR = 'F';
    }
    // 기타 매핑
    else if (gender === 'other' || gender === 'o' || gender === '기타') {
      properties.GDR = 'O';
    }
    // 알 수 없음 (빈 문자열, null, undefined 등)
    else {
      properties.GDR = 'U';
    }
  } else {
    // 성별 정보가 없으면 Unknown
    properties.GDR = 'U';
  }

  // 사용자 타입 (커스텀 프로퍼티)
  if (userData.user_type !== undefined) {
    properties.USER_TYPE = String(userData.user_type);
  }

  // 사용자 레벨 (커스텀 프로퍼티)
  if (userData.level !== undefined) {
    properties.USER_LEVEL = String(userData.level);
  }

  // 가입 후 경과일 (커스텀 프로퍼티)
  if (userData.created_at) {
    const createdDate = new Date(userData.created_at);
    if (!isNaN(createdDate.getTime())) {
      const daysSinceJoin = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceJoin >= 0) {
        properties.DAYS_SINCE_JOIN = String(daysSinceJoin);
      }
    }
  }

  // 활동 점수 (커스텀 프로퍼티)
  if (userData.points !== undefined && userData.points !== null) {
    properties.USER_POINTS = String(userData.points);
  }

  // 선호 지역 (커스텀 프로퍼티)
  if (userData.favorite_area || userData.location) {
    properties.FAVORITE_AREA = String(userData.favorite_area || userData.location);
  }

  return properties;
};

// Adrop 프로퍼티 동기화 캐시 (중복 방지)
const adropSyncCache = new Map();
const ADROP_SYNC_CACHE_DURATION = 10 * 60 * 1000; // 10분 캐시

/**
 * 사용자 데이터 업데이트 시 Adrop에 자동 전송하는 유틸리티
 * 중복 호출 방지 및 캐싱 적용
 * @param {string} userId - 사용자 ID
 * @param {Object} userData - 사용자 데이터
 * @returns {Promise<boolean>} 성공 여부
 */
export const syncUserDataToAdrop = async (userId, userData) => {
  try {
    if (!userId || !userData) {
      return false;
    }

    // 중복 동기화 방지 - 캐시 확인
    const cacheKey = `${userId}_sync`;
    const cached = adropSyncCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ADROP_SYNC_CACHE_DURATION) {
      // Adrop 동기화 스킵 (최근 동기화됨) - 로그 생략
      return true;
    }

    // 사용자 데이터를 Adrop 프로퍼티로 변환
    const properties = convertUserDataToAdropProperties(userData);
    
    if (Object.keys(properties).length === 0) {
      return false;
    }

    // Adrop에 프로퍼티 전송
    const success = await sendUserProperty(userId, properties);
    
    if (success) {
      // 성공 시 캐시에 저장
      adropSyncCache.set(cacheKey, {
        timestamp: Date.now(),
        properties
      });
    }
    
    return success;

  } catch (error) {
    console.error('❌ 사용자 데이터 Adrop 동기화 실패:', error);
    return false;
  }
};

/**
 * 광고 컴포넌트에서 사용할 수 있는 훅
 */
export const useAdrop = () => {
  return {
    isAvailable: isAdropAvailable(),
    requestAd,
    requestBannerAd,
    setUserProperty,
    sendUserProperty,
    sendEvent,
    syncUserDataToAdrop,
    initialize: initializeAdrop
  };
};
